import type { MeView } from '@research-assistant/shared';
import { deleteDB, openDB, type IDBPDatabase } from 'idb';

/**
 * IndexedDB wrapper for small, shell-critical caches.
 *
 * Store layout:
 *   database: `ct-agent`, version 1
 *   object store: `identity`   — keyed by string, stores `MeView` objects.
 *                                Phase 3 uses a single entry under `last-identity`.
 *   database: `ct-agent-thumbnails`, version 1
 *   object store: `artifact-thumbnails` — keyed by `kind:workspaceId:artifactId:revision`, stores a
 *                                {@link StoredThumbnail}; indexed by `storedAt` for pruning.
 *
 * The thumbnails have a database of their own so that adding them never upgrades `ct-agent`. Sign-in
 * waits on `ct-agent`, and a tab still running an older build keeps it open and never lets go for an
 * upgrade, so an upgrade there would hold up sign-in in every new tab until that old tab closed.
 */
const DB_NAME = 'ct-agent';
/** Pre-rebrand DB name. Read once + migrated forward on first open. */
const LEGACY_DB_NAME = 'omni-agent';
const DB_VERSION = 1;
const STORE_IDENTITY = 'identity';
const KEY_LAST_IDENTITY = 'last-identity';
const THUMBNAIL_DB_NAME = 'ct-agent-thumbnails';
const THUMBNAIL_DB_VERSION = 1;
const STORE_THUMBNAILS = 'artifact-thumbnails';
const INDEX_STORED_AT = 'storedAt';

/** The most thumbnails kept. A put past it drops the oldest, so the store stays a bounded cache. */
export const ARTIFACT_THUMBNAIL_LIMIT = 300;

/**
 * One cached thumbnail. The bytes are kept as an ArrayBuffer with the type beside them rather than as a
 * Blob, because an ArrayBuffer stores the same way in every browser IndexedDB, while Blob values have had
 * engine-specific failures.
 */
interface StoredThumbnail {
  bytes: ArrayBuffer;
  type: string;
  storedAt: number;
}

interface CtAgentDBSchema {
  [STORE_IDENTITY]: MeView;
}

let dbPromise: Promise<IDBPDatabase<unknown>> | null = null;
let thumbnailDbPromise: Promise<IDBPDatabase<unknown>> | null = null;

async function openAndMigrate(): Promise<IDBPDatabase<unknown>> {
  const db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_IDENTITY)) {
        db.createObjectStore(STORE_IDENTITY);
      }
    },
  });
  // One-time forward migration from the pre-rebrand `omni-agent` DB. Skip
  // when the new DB already has data (i.e. user has reloaded since the
  // rebrand), or when `indexedDB.databases()` says the legacy DB isn't
  // there. We never blindly open the legacy DB — that would create an
  // empty placeholder and we'd then have to clean it up.
  if (
    typeof indexedDB !== 'undefined' &&
    typeof indexedDB.databases === 'function'
  ) {
    try {
      const existing = await db.get(STORE_IDENTITY, KEY_LAST_IDENTITY);
      if (existing === undefined) {
        const list = await indexedDB.databases();
        const legacyPresent = list.some((d) => d.name === LEGACY_DB_NAME);
        if (legacyPresent) {
          const legacy = await openDB(LEGACY_DB_NAME);
          if (legacy.objectStoreNames.contains(STORE_IDENTITY)) {
            const legacyValue = await legacy.get(
              STORE_IDENTITY,
              KEY_LAST_IDENTITY,
            );
            if (legacyValue !== undefined) {
              await db.put(STORE_IDENTITY, legacyValue, KEY_LAST_IDENTITY);
            }
          }
          legacy.close();
          await deleteDB(LEGACY_DB_NAME);
        }
      }
    } catch {
      /* migration is best-effort — identity will simply be re-fetched */
    }
  }
  return db;
}

function getDB(): Promise<IDBPDatabase<unknown>> {
  if (dbPromise === null) {
    dbPromise = openAndMigrate();
  }
  return dbPromise;
}

export async function getCachedIdentity(): Promise<MeView | null> {
  const db = await getDB();
  const value = (await db.get(STORE_IDENTITY, KEY_LAST_IDENTITY)) as
    CtAgentDBSchema[typeof STORE_IDENTITY] | undefined;
  return value ?? null;
}

export async function setCachedIdentity(identity: MeView): Promise<void> {
  const db = await getDB();
  await db.put(STORE_IDENTITY, identity, KEY_LAST_IDENTITY);
}

/**
 * The thumbnail database, opened once per tab. When a newer build asks to upgrade it, this tab closes its
 * connection rather than hold that upgrade up, and its thumbnails read as not cached from then on.
 */
function getThumbnailDB(): Promise<IDBPDatabase<unknown>> {
  if (thumbnailDbPromise === null) {
    thumbnailDbPromise = openThumbnailDB();
  }
  return thumbnailDbPromise;
}

async function openThumbnailDB(): Promise<IDBPDatabase<unknown>> {
  const db = await openDB(THUMBNAIL_DB_NAME, THUMBNAIL_DB_VERSION, {
    upgrade(database) {
      database
        .createObjectStore(STORE_THUMBNAILS)
        .createIndex(INDEX_STORED_AT, INDEX_STORED_AT);
    },
    blocking() {
      db.close();
      thumbnailDbPromise = null;
    },
  });
  return db;
}

/**
 * The thumbnail stored under this key — `kind:workspaceId:artifactId:revision`, so a new version never
 * reads the old drawing — or null. Any IndexedDB failure (private mode, a quota error, a database a newer
 * build upgraded) reads as "not cached": a thumbnail is then drawn again rather than the tile breaking.
 */
export async function getArtifactThumbnail(key: string): Promise<Blob | null> {
  try {
    const db = await getThumbnailDB();
    const stored = (await db.get(STORE_THUMBNAILS, key)) as
      StoredThumbnail | undefined;
    return stored ? new Blob([stored.bytes], { type: stored.type }) : null;
  } catch {
    return null;
  }
}

/** Store a thumbnail under its revision key and drop the oldest past the limit. A failure is ignored. */
export async function putArtifactThumbnail(
  key: string,
  thumbnail: Blob,
): Promise<void> {
  try {
    const stored: StoredThumbnail = {
      bytes: await thumbnail.arrayBuffer(),
      type: thumbnail.type,
      storedAt: Date.now(),
    };
    const db = await getThumbnailDB();
    const transaction = db.transaction(STORE_THUMBNAILS, 'readwrite');
    await transaction.store.put(stored, key);
    let excess = (await transaction.store.count()) - ARTIFACT_THUMBNAIL_LIMIT;
    let cursor = await transaction.store.index(INDEX_STORED_AT).openCursor();
    while (cursor && excess > 0) {
      await cursor.delete();
      excess -= 1;
      cursor = await cursor.continue();
    }
    await transaction.done;
  } catch {
    return;
  }
}

/** Test-only helper to reset the module-level connection promise. */
export function _resetIdbForTests(): void {
  dbPromise = null;
  thumbnailDbPromise = null;
}
